import { useState } from "react";
import PropTypes from "prop-types";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

// Sample data for documents
const documentsData = [
  {
    id: 1,
    name: "Purchase Agreement.pdf",
    type: "Legal",
    property: "Sunset Apartments",
    uploadedBy: "John Smith",
    uploadDate: "2025-04-15",
    size: "1.2 MB",
    status: "Active",
  },
  {
    id: 2,
    name: "Tenant Lease 2025-07.docx",
    type: "Lease",
    property: "Sunset Apartments",
    uploadedBy: "Sarah Johnson",
    uploadDate: "2025-04-12",
    size: "850 KB",
    status: "Active",
  },
  {
    id: 3,
    name: "Property Inspection Report.pdf",
    type: "Inspection",
    property: "Oakwood Heights",
    uploadedBy: "Michael Brown",
    uploadDate: "2025-04-10",
    size: "3.5 MB",
    status: "Requires Review",
  },
  {
    id: 4,
    name: "Insurance Policy.pdf",
    type: "Insurance",
    property: "Riverside Condos",
    uploadedBy: "John Smith",
    uploadDate: "2025-04-08",
    size: "2.1 MB",
    status: "Active",
  },
  {
    id: 5,
    name: "Property Tax Assessment.pdf",
    type: "Financial",
    property: "Oakwood Heights",
    uploadedBy: "Sarah Johnson",
    uploadDate: "2025-04-05",
    size: "1.8 MB",
    status: "Active",
  },
  {
    id: 6,
    name: "Maintenance Agreement.docx",
    type: "Maintenance",
    property: "Riverside Condos",
    uploadedBy: "Michael Brown",
    uploadDate: "2025-04-01",
    size: "950 KB",
    status: "Expires Soon",
  },
  {
    id: 7,
    name: "HOA Regulations.pdf",
    type: "HOA",
    property: "Sunset Apartments",
    uploadedBy: "John Smith",
    uploadDate: "2025-03-28",
    size: "2.3 MB",
    status: "Active",
  },
];

// Sample activities for timeline
const activitiesData = [
  { id: 1, user: "John Smith", action: "uploaded", document: "Purchase Agreement.pdf", time: "2 hours ago" },
  { id: 2, user: "Sarah Johnson", action: "viewed", document: "Tenant Lease 2025-07.docx", time: "5 hours ago" },
  { id: 3, user: "Michael Brown", action: "downloaded", document: "Property Inspection Report.pdf", time: "Yesterday" },
  { id: 4, user: "Sarah Johnson", action: "updated", document: "Insurance Policy.pdf", time: "2 days ago" },
];

function Documents() {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterProperty, setFilterProperty] = useState("All");
  const [filterType, setFilterType] = useState("All");
  
  // Filter documents based on search query, property filter, and type filter
  const filteredDocuments = documentsData.filter((doc) => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          doc.property.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          doc.uploadedBy.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProperty = filterProperty === "All" || doc.property === filterProperty;
    const matchesType = filterType === "All" || doc.type === filterType;
    
    return matchesSearch && matchesProperty && matchesType;
  });
  
  // Get unique property names for filter dropdown
  const properties = ["All", ...new Set(documentsData.map(doc => doc.property))];
  
  // Get unique document types for filter dropdown
  const documentTypes = ["All", ...new Set(documentsData.map(doc => doc.type))];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card sx={{ padding: 3, mb: 3 }}>
          <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <div>
              <ArgonTypography variant="h4" fontWeight="medium">
                Property Documents
              </ArgonTypography>
              <ArgonTypography variant="body2" color="text" mt={1}>
                Upload, view, and manage property-related documents here.
              </ArgonTypography>
            </div>
            <ArgonButton 
              variant="contained" 
              color="primary"
              startIcon={<CloudUploadIcon />}
            >
              Upload New Document
            </ArgonButton>
          </ArgonBox>
          
          <ArgonBox mb={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="property-filter-label">Property</InputLabel>
                  <Select
                    labelId="property-filter-label"
                    value={filterProperty}
                    label="Property"
                    onChange={(e) => setFilterProperty(e.target.value)}
                  >
                    {properties.map((property) => (
                      <MenuItem key={property} value={property}>
                        {property}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="type-filter-label">Document Type</InputLabel>
                  <Select
                    labelId="type-filter-label"
                    value={filterType}
                    label="Document Type"
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    {documentTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </ArgonBox>
          
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="documents table">
              <TableHead>
                <TableRow>
                  <TableCell>Document Name</TableCell>
                  <TableCell>Property</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Uploaded By</TableCell>
                  <TableCell>Upload Date</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDocuments.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell component="th" scope="row">
                      {doc.name}
                    </TableCell>
                    <TableCell>{doc.property}</TableCell>
                    <TableCell>{doc.type}</TableCell>
                    <TableCell>{doc.uploadedBy}</TableCell>
                    <TableCell>{doc.uploadDate}</TableCell>
                    <TableCell>{doc.size}</TableCell>
                    <TableCell>
                      <Chip 
                        label={doc.status} 
                        color={
                          doc.status === "Active" ? "success" :
                          doc.status === "Expires Soon" ? "warning" :
                          doc.status === "Requires Review" ? "info" : 
                          "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" title="View">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" title="Download">
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" title="Delete">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mt={3}>
            <ArgonTypography variant="body2" color="text">
              Showing {filteredDocuments.length} of {documentsData.length} documents
            </ArgonTypography>
            <ArgonButton variant="outlined" color="info" size="small">
              View All Documents
            </ArgonButton>
          </ArgonBox>
        </Card>
        
        <Card sx={{ padding: 3 }}>
          <ArgonTypography variant="h5" fontWeight="medium" mb={2}>
            Recent Activity
          </ArgonTypography>
          <SimpleTimeline activities={activitiesData} />
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

// Simple Timeline Component
function SimpleTimeline({ activities }) {
  return (
    <Box sx={{ ml: 2 }}>
      {activities.map((activity) => (
        <Box 
          key={activity.id} 
          sx={{ 
            display: 'flex', 
            mb: 2, 
            pb: 2, 
            borderBottom: '1px solid #eee',
            '&:last-child': { borderBottom: 'none' }
          }}
        >
          <Box 
            sx={{ 
              width: 10, 
              height: 10, 
              borderRadius: '50%', 
              bgcolor: 'primary.main', 
              mt: 1.5, 
              mr: 2 
            }} 
          />
          <Box>
            <ArgonTypography variant="body2" fontWeight="medium">
              {activity.user} {activity.action} &ldquo;{activity.document}&rdquo;
            </ArgonTypography>
            <ArgonTypography variant="caption" color="text">
              {activity.time}
            </ArgonTypography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// PropTypes validation for SimpleTimeline component
SimpleTimeline.propTypes = {
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      user: PropTypes.string.isRequired,
      action: PropTypes.string.isRequired,
      document: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default Documents;